// Google Tag Manager for the app: the same container as www.fieldnexus.com.
//
// Loaded from here rather than index.html so one list decides where it never
// runs. Some pages carry secrets in the URL (password reset, invitation, and
// form submission tokens) and others belong to our customers' customers
// (invoice payment, quote review, public booking). GTM tags read the page URL,
// so on those pages GTM isn't loaded at all, and navigating to one isn't
// reported.
//
// The app is a single page, so moving between screens never reloads it and
// GTM would only see the first page view. Navigo changes the address with
// history.pushState / replaceState, so we watch those (plus back/forward) and
// push a `virtual_page_view` event to the dataLayer whenever the path changes.
// Only the path is sent: query strings carry list filters and, on /login, the
// email address from the website's trial handoff.
//
// GTM setup: a Custom Event trigger on `virtual_page_view` firing a GA4 event
// (page_view) with page_location = {{DLV - page_location}}.

const GTM_ID = 'GTM-MXRRFGBN';
const EVENT = 'virtual_page_view';

// Never load or report GTM on these paths (see Router.js).
const UNTRACKED_PATHS = [
  /^\/password-reset\//,
  /^\/users\/invitation\//,
  /^\/forms\/[^/]+\/form_submission\//,
  /^\/pay\//,
  /^\/review\//,
  /^\/booking_public\//,
  /^\/booking-confirmation/,
];

export const isUntrackedPath = (path) => UNTRACKED_PATHS.some((pattern) => pattern.test(path));

const loadContainer = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

const watchPageViews = () => {
  let lastPath = window.location.pathname;

  const report = () => {
    const path = window.location.pathname;
    if (path === lastPath) return; // filter or query-only change
    lastPath = path;
    if (isUntrackedPath(path)) return;
    // Let the new screen render (and set its title) before GTM reads the page.
    window.setTimeout(() => {
      window.dataLayer.push({
        event: EVENT,
        page_path: path,
        page_location: window.location.origin + path,
        page_title: document.title,
      });
    }, 0);
  };

  const originals = {};
  ['pushState', 'replaceState'].forEach((method) => {
    const original = window.history[method];
    originals[method] = original;
    window.history[method] = function (...args) {
      const result = original.apply(this, args);
      report();
      return result;
    };
  });
  window.addEventListener('popstate', report);

  return () => {
    Object.assign(window.history, originals);
    window.removeEventListener('popstate', report);
  };
};

// Call once, after the first route has settled, so a redirect while loading
// isn't counted as a second page view. `hostname` is only overridden in tests.
// Returns a cleanup function (used by tests).
export const startTagManager = ({ hostname = window.location.hostname } = {}) => {
  // Keep dev traffic out of analytics.
  if (hostname === 'localhost') return () => {};
  if (isUntrackedPath(window.location.pathname)) return () => {};
  loadContainer();
  return watchPageViews();
};
